import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretDown, CaretRight, Copy } from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ToolStatusBadge } from './ToolStatusBadge';
import { AlumniCardGrid } from './AlumniCard';
import type { ChatMessage } from '../hooks/useChatStream';

function parseThinkingBlock(content: string): { thinking: string | null; text: string } {
  // Matches one or more lines starting with ">" at the beginning
  const thinkingRegex = /^(?:>[^\n]*\n?)+/m;
  const match = content.match(thinkingRegex);
  if (match && match.index === 0 && match[0].toLowerCase().includes('thinking')) {
    const rawBlock = match[0];
    const thinking = rawBlock.replace(/^>\s?/gm, '').trim();
    const text = content.substring(rawBlock.length).trim();
    return { thinking, text };
  }
  return { thinking: null, text: content };
}

function ThinkingDropdown({ content, isStreaming }: { content: string, isStreaming?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="mb-4 mt-2 bg-slate-50/50 border border-slate-200/60 rounded-xl overflow-hidden transition-all duration-200 w-full">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-slate-100/50 transition-colors text-slate-600"
      >
        <div className="flex items-center justify-center text-slate-400">
          {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        </div>
        <span className="text-sm text-gray-400">
          {isStreaming && !isOpen ? 'Thinking...' : 'Thought process'}
        </span>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-5 py-4 border-t border-slate-100 bg-white text-slate-600 text-[14px] leading-relaxed italic font-inter whitespace-pre-wrap">
              {content}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Parse ```card ... ``` fences from assistant content
function parseCardFences(content: string): { cards: any | null; text: string } {
  const cardRegex = /```card\n([\s\S]*?)\n```/;
  const match = content.match(cardRegex);
  if (!match) return { cards: null, text: content };
  try {
    const cards = JSON.parse(match[1]);
    const text = content.replace(cardRegex, '').trim();
    return { cards, text };
  } catch {
    return { cards: null, text: content };
  }
}

function UserBubble({ content }: { content: string }) {
  return (
    <div className="flex flex-col items-end gap-1.5 w-full mb-6 mt-4">
      <span className="text-[11px] font-semibold text-slate-400 mr-2 uppercase tracking-widest">You</span>
      <div className="max-w-3xl bg-slate-100 text-slate-800 rounded-2xl rounded-tr-sm px-6 py-4 text-[15px] leading-[1.7] font-inter">
        {content}
      </div>
    </div>
  );
}

function AssistantBubble({ msg }: { msg: ChatMessage }) {
  const { cards, text: textAfterCards } = parseCardFences(msg.content);
  const { thinking, text: finalMarkdown } = parseThinkingBlock(textAfterCards);

  return (
    <div className="flex flex-col items-start gap-2 w-full mb-10">
    

      <div className="max-w-3xl w-full">
        {/* Tool badge */}
        {msg.activeTool && <ToolStatusBadge tool={msg.activeTool} done={false} />}
        {!msg.activeTool && msg.tool_used && <ToolStatusBadge tool={msg.tool_used} done={true} />}

        {/* Alumni cards from card fence */}
        {cards?.type === 'alumni' && cards.items?.length > 0 && (
          <div className="mt-4 mb-6">
            <AlumniCardGrid items={cards.items} />
          </div>
        )}

        {/* Thinking Block */}
        {thinking && (
          <ThinkingDropdown content={thinking} isStreaming={msg.isStreaming} />
        )}

        {/* Markdown content */}
        {(finalMarkdown || msg.isStreaming) && (
          <div className="text-[15px] text-slate-800 leading-[1.75] font-inter mt-2 w-full px-6 py-5  relative group">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1.5">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-1.5">{children}</ol>,
                li: ({ children }) => <li className="text-[15px] pl-1">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
                code: ({ children }) => (
                  <code className="bg-slate-100/80 text-slate-700 px-1.5 py-0.5 rounded-md text-[13px] font-mono border border-slate-200/50">{children}</code>
                ),
                pre: ({ children, ...props }: any) => {
                  const codeText = props.node?.children?.[0]?.children?.[0]?.value || '';
                  return (
                  <div className="my-5 rounded-xl overflow-hidden border border-slate-200 bg-[#0f172a] shadow-md w-full max-w-full relative group/code">
                    <div className="flex items-center justify-between px-4 py-2 bg-slate-800/80 border-b border-slate-700/50 backdrop-blur-md">
                      <div className="flex gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-rose-400/80" />
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                      </div>
                      <button 
                        onClick={() => navigator.clipboard.writeText(codeText)}
                        className="text-slate-400 hover:text-white transition-colors opacity-0 group-hover/code:opacity-100 flex items-center gap-1 text-xs font-medium bg-slate-700/50 px-2 py-1 rounded-md"
                      >
                        <Copy size={14} weight="bold" /> Copy
                      </button>
                    </div>
                    <pre className="p-4 overflow-x-auto text-[13px] font-mono leading-relaxed text-slate-50">{children}</pre>
                  </div>
                )},
                table: ({ children }) => (
                  <div className="overflow-x-auto mb-5 border border-slate-200/80 rounded-xl w-full max-w-full shadow-sm">
                    <table className="w-full text-sm border-collapse bg-white">{children}</table>
                  </div>
                ),
                th: ({ children }) => (
                  <th className="text-left px-4 py-3 font-semibold text-slate-800 bg-slate-50/80 border-b border-slate-200/80">{children}</th>
                ),
                td: ({ children }) => (
                  <td className="px-4 py-3 text-slate-600 border-b border-slate-100/50 last:border-0 bg-white">{children}</td>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-orange-300 pl-5 py-1.5 my-4 text-slate-600 italic bg-orange-50/30 rounded-r-xl">
                    {children}
                  </blockquote>
                )
              }}
            >
              {finalMarkdown || ''}
            </ReactMarkdown>
          </div>
        )}

        {/* Loading skeleton when no content yet */}
        {msg.isStreaming && !finalMarkdown && !thinking && !cards && !msg.activeTool && (
          <div className="mt-3 flex items-center gap-2 px-1 py-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full bg-gradient-to-tr from-orange-500 to-amber-400 shadow-[0_2px_4px_rgba(249,115,22,0.2)]"
                animate={{ 
                  y: [0, -6, 0],
                  scale: [1, 1.1, 1],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.15,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface MessageListProps {
  messages: ChatMessage[];
}

export function MessageList({ messages }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScroll, setShowScroll] = useState(false);

  // Auto-scroll on new message
  useEffect(() => {
    // Only auto-scroll if we are already near the bottom, or if it's a new message
    if (!showScroll) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    // Show button if scrolled up more than 100px from bottom
    if (scrollHeight - scrollTop - clientHeight > 100) {
      setShowScroll(true);
    } else {
      setShowScroll(false);
    }
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div 
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto px-8 lg:px-24 py-8 relative"
    >
      <div className="max-w-4xl mx-auto flex flex-col min-h-full pb-10">
        <AnimatePresence initial={false}>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="w-full"
            >
              {msg.role === 'human' ? (
                <UserBubble content={msg.content} />
              ) : (
                <AssistantBubble msg={msg} />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={bottomRef} className="h-4 shrink-0" />
      </div>

      {/* Floating Scroll to Bottom Button */}
      <AnimatePresence>
        {showScroll && (
          <motion.button
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            onClick={scrollToBottom}
            className="fixed bottom-[180px] left-1/2 -translate-x-1/2 z-30 bg-white/90 backdrop-blur-md shadow-xl border border-slate-200/60 rounded-full p-2.5 text-slate-500 hover:text-slate-800 hover:bg-white transition-all flex items-center justify-center group"
          >
            <CaretDown size={20} weight="bold" className="group-hover:translate-y-0.5 transition-transform" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 15) return 'Good Afternoon';
  return 'Good Evening';
}

const userName = localStorage.getItem('name') || 'Admin';

export function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center w-full mb-6 max-w-2xl mx-auto">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
        className="w-full"
      >
        
        <h2 className="font-outfit font-semibold text-slate-800 text-3xl mb-3 tracking-tight">
          {getGreeting()} {userName.split(' ')[0]}, how can I help?
        </h2>
        <p className="text-[15px] text-slate-500 font-inter leading-relaxed">
          I'm LinkAlum AI, your workspace assistant. Ask me anything about alumni, donors, proposals, or projects.
        </p>
      </motion.div>
    </div>
  );
}
